import { useClippableObjectsBox } from "@/hooks/use-clippable-objects-box";
import { useCurrentAreaClippingBox } from "@/hooks/use-object-bounding-box";
import {
  CadModelObject,
  GaussianSplatsObject,
  PanoObject,
  PhotoObject,
  PointCloudObject,
  SheetObject,
} from "@/object-cache";
import {
  isCadModelObject,
  isGaussianSplatsObject,
  isOrthomosaicObject,
  isPanoObject,
  isPhotoObject,
  isPointCloudObject,
  UnknownObject,
} from "@/object-cache-type-guard";
import { selectZoomLensActive } from "@/store/measurement-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { IElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { assert, LodFloorPlan, OrientedBoundingBox } from "@faro-lotv/lotv";
import { useLotvDispose } from "@faro-lotv/spatial-ui";
import { useMemo } from "react";
import { Box3 } from "three";
import { isFlyWalkImageElement } from "../flywalk-types";

export type FlyWalkRenderables = {
  /** Current pano to be rendered */
  pano: PanoObject | null;
  /** Previous pano to optionally render during a pano-image transition */
  previousPano: PanoObject | null;
  /** Current point cloud to be rendered */
  pointCloud: PointCloudObject | null;
  /** Current CAD model to be rendered */
  cadModel: CadModelObject | null;
  /** Current Gaussian splats to be rendered */
  gaussianSplats: GaussianSplatsObject | null;
  /** Current 2D photo to be rendered */
  photo2D: PhotoObject | null;
  /** Previous 2D photo to be rendered during a photo-image transition*/
  previousPhoto2D: PhotoObject | null;
  /** Current orthomosaic to be rendered */
  orthomosaic: SheetObject | null;
  /** Previous orthomosaic to be rendered during an orthomosaic-image transition */
  previousOrthomosaic: SheetObject | null;
  /** Models clickable by tools */
  clickableByTools: UnknownObject[];
  /** Models clickable by controls */
  clickableByControls: UnknownObject[];
  /** Current area clipping box */
  areaBox: OrientedBoundingBox | undefined;
  /** Bounding box of all clippable objects in the scene */
  allModelsBox: Box3;
};

/**
 * Computes the current renderable objects with their concrete types, their bounding box, and the
 * list of clickable objects. If needed, allocates 'views' of given objects to support a
 * splitscreen scenario.
 *
 * @param theMainObject The main object to be rendered in fly walk mode
 * @param theOverlayObject The overlay object to be rendered in fly walk mode
 * @param thePreviousMainObject The object that was previously rendered as the main object in fly walk mode
 * @param sceneFilter the current scene filter
 * @param activeSheets The currently active sheets in the scene
 * @param isSecondaryView Whether the view is a secondary view
 * @returns The current renderable objects with their correct types
 */
export function useDowncastObjects(
  theMainObject: UnknownObject | null | undefined,
  theOverlayObject: PointCloudObject | CadModelObject | null,
  thePreviousMainObject: UnknownObject | null,
  sceneFilter: SceneFilter,
  activeSheets: IElementGenericImgSheet[],
  isSecondaryView: boolean = false,
): FlyWalkRenderables {
  // If data is being dispatched for a secondary view, create 'views' of objects
  const mainObject = useMaybeCreateView(theMainObject, isSecondaryView);
  const previousMainObject = useMaybeCreateView(
    thePreviousMainObject,
    isSecondaryView,
  );
  const overlayObject = useMaybeCreateView(theOverlayObject, isSecondaryView);
  assert(
    isOverlayObject(overlayObject),
    "Expected the overlay object to be a point cloud or a CAD model",
  );

  // To facilitate dispatch of renderable objects to the rendering pipeline,
  // we add a 'isPreviousImage' flag to the userData of the 3js object so that
  // we know when an image->image transition is needed.
  if (mainObject) {
    mainObject.userData.isPreviousImage = false;
  }

  const renderables = useComputeRenderables(
    mainObject,
    overlayObject,
    previousMainObject,
  );

  const areaBox = useCurrentAreaClippingBox();

  const allModelsBox = useClippableObjectsBox(
    renderables.pointCloud,
    renderables.cadModel,
    activeSheets,
  );

  return useComposeResult(renderables, areaBox, allModelsBox, sceneFilter);
}

function useComposeResult(
  renderables: FlyWalkRenderables,
  areaBox: OrientedBoundingBox | undefined,
  allModelsBox: Box3,
  sceneFilter: SceneFilter,
): FlyWalkRenderables {
  const zoomLensOn = useAppSelector(selectZoomLensActive);

  return useMemo(() => {
    const ret = {
      ...renderables,
      areaBox,
      allModelsBox: allModelsBox.clone(),
    };
    computeClickableModels(ret, sceneFilter, zoomLensOn);
    return ret;
  }, [renderables, areaBox, allModelsBox, sceneFilter, zoomLensOn]);
}

function computeClickableModels(
  result: FlyWalkRenderables,
  sceneFilter: SceneFilter,
  zoomLensOn: boolean,
): void {
  result.clickableByTools = [];
  result.clickableByControls = [];
  if (sceneFilter === SceneFilter.Pano && result.pano && !zoomLensOn) {
    result.clickableByTools.push(result.pano);
    result.clickableByControls.push(result.pano);
  }
  if (
    (sceneFilter === SceneFilter.PointCloud ||
      sceneFilter === SceneFilter.Overlay ||
      zoomLensOn) &&
    result.pointCloud
  ) {
    result.clickableByTools.push(result.pointCloud);
    result.clickableByControls.push(result.pointCloud);
  }
  if (
    (sceneFilter === SceneFilter.Cad || sceneFilter === SceneFilter.Overlay) &&
    result.cadModel
  ) {
    result.clickableByTools.push(result.cadModel);
    result.clickableByControls.push(result.cadModel);
  }
  // Gaussian splats are not clickable by tools
  if (sceneFilter === SceneFilter.GaussianSplats && result.gaussianSplats) {
    result.clickableByControls.push(result.gaussianSplats);
  }
}

function useComputeRenderables(
  mainObject: UnknownObject | null,
  overlayObject: PointCloudObject | CadModelObject | null,
  previousMainObject: UnknownObject | null,
): FlyWalkRenderables {
  return useMemo<FlyWalkRenderables>(() => {
    const orthomosaic =
      mainObject && isOrthomosaicObject(mainObject) ? mainObject : null;
    // If we have an orthomosaic to render, we flag it with a boolean.
    // It is the only way to make it possible that the rendering pipeline can distinguish it
    // from the sheets, and dispatch it to the correct rendering pass.
    if (orthomosaic) {
      orthomosaic.userData.isOrthomosaic = true;
    }
    let pointCloud: PointCloudObject | null = null;
    let cadModel: CadModelObject | null = null;
    if (mainObject) {
      if (isPointCloudObject(mainObject)) {
        pointCloud = mainObject;
      } else if (isCadModelObject(mainObject)) {
        cadModel = mainObject;
      }
    }
    if (overlayObject) {
      if (pointCloud === null && isPointCloudObject(overlayObject)) {
        pointCloud = overlayObject;
      } else if (isCadModelObject(overlayObject)) {
        cadModel = overlayObject;
      }
    }

    // Early result that does not account for the previous main object.
    const result: FlyWalkRenderables = {
      pano: mainObject && isPanoObject(mainObject) ? mainObject : null,
      pointCloud,
      cadModel,
      gaussianSplats:
        mainObject && isGaussianSplatsObject(mainObject) ? mainObject : null,
      photo2D: mainObject && isPhotoObject(mainObject) ? mainObject : null,
      orthomosaic,
      previousPano: null,
      previousPhoto2D: null,
      previousOrthomosaic: null,
      clickableByTools: [],
      clickableByControls: [],
      areaBox: undefined,
      allModelsBox: new Box3().expandByScalar(1),
    };

    if (
      !previousMainObject ||
      previousMainObject.iElement.id === mainObject?.iElement.id
    ) {
      return result;
    }

    const mainIsImg = mainObject && isFlyWalkImageElement(mainObject.iElement);

    // If the previous main object exists and is different from the current main object,
    // it is down-casted to its concrete type and assigned to the correct prop in renderables.
    if (isPanoObject(previousMainObject)) {
      if (mainIsImg) {
        previousMainObject.userData.isPreviousImage = true;
        result.previousPano = previousMainObject;
      } else {
        result.pano = previousMainObject;
      }
    } else if (
      result.pointCloud === null &&
      isPointCloudObject(previousMainObject)
    ) {
      result.pointCloud = previousMainObject;
    } else if (
      result.cadModel === null &&
      isCadModelObject(previousMainObject)
    ) {
      result.cadModel = previousMainObject;
    } else if (
      result.gaussianSplats === null &&
      isGaussianSplatsObject(previousMainObject)
    ) {
      result.gaussianSplats = previousMainObject;
    } else if (isPhotoObject(previousMainObject)) {
      if (mainIsImg) {
        previousMainObject.userData.isPreviousImage = true;
        result.previousPhoto2D = previousMainObject;
      } else {
        result.photo2D = previousMainObject;
      }
    } else if (isOrthomosaicObject(previousMainObject)) {
      if (mainIsImg) {
        previousMainObject.userData.isPreviousImage = true;
        result.previousOrthomosaic = previousMainObject;
      } else {
        result.orthomosaic = previousMainObject;
      }
    }
    return result;
  }, [mainObject, overlayObject, previousMainObject]);
}

function useMaybeCreateView(
  source: UnknownObject | null | undefined,
  isSecondaryView: boolean,
): UnknownObject | null {
  const view = useMemo(() => {
    if (!source) {
      return null;
    }
    if (!isSecondaryView) {
      return source;
    }
    if (
      isPointCloudObject(source) ||
      isPanoObject(source) ||
      isPhotoObject(source) ||
      source instanceof LodFloorPlan
    ) {
      return Object.assign(source.createView(), { iElement: source.iElement });
    }
    return null;
  }, [source, isSecondaryView]);
  useLotvDispose(view === source ? null : view);
  return view;
}

function isOverlayObject(
  o: UnknownObject | null,
): o is PointCloudObject | CadModelObject | null {
  return o === null || isPointCloudObject(o) || isCadModelObject(o);
}
